import { eventNames, useCoreEvents } from '@carvana/core-events';
import LinkButton from '@carvana/ds-link-button';
import Radio, { RadioGroup } from '@carvana/ds-radio';
import { OnEscapeListener } from '@carvana/merch-components';
import { ChevronDown } from 'lucide-react';
import { useCallback, useContext, useRef } from 'react';
import FocusLock from 'react-focus-lock';
import DsModal from '@/common/DsModal';
import Portal from '@/common/Portal';
import { modalZIndex } from '~/constants/common/zIndex';
import COOKIES from '~/constants/cookies';
import { useIsDesktop } from '~/context/common/DeviceContext';
import TradeContext from '~/context/common/TradeContext';
import { BrowserCookies } from '~/utilities';

type OwnProps = {
  isOpen: boolean;
  setIsOpen: (isOpen: boolean) => void;
};

export default function MultiTradeSelect({ isOpen, setIsOpen }: OwnProps) {
  const {
    trades: { data: trades },
    selectedTrade,
    setSelectedTrade
  } = useContext(TradeContext);

  const { emit } = useCoreEvents();
  const isDesktop = useIsDesktop();
  const selectedTradeId = selectedTrade?.id ?? '';
  const anchorRef = useRef<HTMLDivElement>(null);

  const onClose = useCallback(() => setIsOpen(false), [setIsOpen]);

  const handleSelect = useCallback(
    (tradeId: string) => {
      const selectedTradeData = trades.find(t => t.id === tradeId);

      if (selectedTradeData) {
        BrowserCookies.set(COOKIES.preferredTrade, selectedTradeData.id, {
          expires: 365
        });
        setSelectedTrade(selectedTradeData);
        onClose();

        emit(eventNames.favorites.mfe.refresh, {
          source: 'app',
          changed: 'preferredTrade'
        });
      }
    },
    [emit, onClose, setSelectedTrade, trades]
  );

  if (trades.length <= 1) {
    return null;
  }

  const control = (
    <LinkButton
      variant="default"
      size="sm"
      underline={false}
      onClick={() => setIsOpen(!isOpen)}
      data-analytics-id="Trades Dropdown"
      data-testid="multi-trade-select"
      className="flex items-center gap-xs"
    >
      Switch Vehicle
      <ChevronDown
        size={16}
        aria-hidden="true"
        className={isOpen ? 'rotate-180' : ''}
      />
    </LinkButton>
  );

  if (!isDesktop) {
    return (
      <>
        {control}
        <DsModal
          modalHeader="Switch Vehicle"
          onClose={onClose}
          isOpen={isOpen}
          mobileStyle="inset"
          desktopWidth="328px"
          divLine={false}
          classes={{ body: 'pt-md' }}
        >
          <RadioGroup<string>
            value={selectedTradeId}
            onChange={handleSelect}
            className="flex w-full flex-col gap-md"
          >
            {trades.map(trade => (
              <Radio
                key={trade.id}
                id={trade.id}
                value={trade.id}
                selected={selectedTradeId === trade.id}
                data-analytics-id="Trade Selected"
              >
                {trade.vehicle.year} {trade.vehicle.make} {trade.vehicle.model}
              </Radio>
            ))}
          </RadioGroup>
        </DsModal>
      </>
    );
  }

  const anchorRect = anchorRef.current?.getBoundingClientRect();

  return (
    <div ref={anchorRef} className="relative">
      {control}
      <Portal id="trade-select-dropdown" open={isOpen}>
        {/* group="modal" coordinates with common/Modal's FocusLock so stacking */}
        {/* overlays don't fight for focus. */}
        <FocusLock returnFocus group="modal">
          <OnEscapeListener onEscape={onClose}>
            <div
              className="fixed inset-0 bg-canvas-overlay"
              style={{ zIndex: modalZIndex }}
              data-testid="multi-trade-select-overlay"
              onClick={() => setIsOpen(false)}
            />
            <div
              className="fixed mt-xs w-[260px] rounded-md border border-border-weak bg-background-default p-md shadow-lg"
              style={{
                top: anchorRect ? anchorRect.bottom : 0,
                left: anchorRect ? anchorRect.left : 0,
                zIndex: modalZIndex + 1
              }}
            >
              <RadioGroup<string>
                value={selectedTradeId}
                onChange={handleSelect}
                className="flex flex-col gap-xs"
              >
                {trades.map(trade => (
                  <Radio
                    key={trade.id}
                    id={trade.id}
                    value={trade.id}
                    selected={selectedTradeId === trade.id}
                    data-analytics-id="Trade Selected"
                  >
                    {trade.vehicle.year} {trade.vehicle.make}{' '}
                    {trade.vehicle.model}
                  </Radio>
                ))}
              </RadioGroup>
            </div>
          </OnEscapeListener>
        </FocusLock>
      </Portal>
    </div>
  );
}
